import React from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory , Redirect } from 'react-router-dom';

import Alert from 'reactstrap/lib/Alert';
import Input from 'reactstrap/lib/Input';
import Label from 'reactstrap/lib/Label';



import {
  ConversionStatusType,
  getConversionStatus,
  deleteCheckoutErrors,
  SuccessfulConversionStatuses,
} from '@ttstr/api/cart';
import { LoadingSpinner } from '@ttstr/components';
import StripeCheckout from '@ttstr/components/Checkout/StripeCheckout';
import AbsoluteRedirect from '@ttstr/components/Routing/AbsoluteRedirect';
import { Button } from 'reactstrap';

const RETRY_AFTER_MS = 5000;

interface OwnProps {
  cartToken: string;
}

type Props = Readonly<OwnProps>;

const ConversionStatusComponent: React.FC<Props> = ({ cartToken }) => {
  const timeoutId = React.useRef<NodeJS.Timeout>();
  const [error, setError] = React.useState<Error>(null);
  const [checkoutErrors, setCheckoutErrors] = React.useState<Error>(null);
  const [loading, setLoading] = React.useState(true);
  const [conversion, setConversion] = React.useState<SuccessfulConversionStatuses>(null);
  const { t } = useTranslation();
  const history = useHistory();
  const isMounted = React.useRef(true);

  React.useEffect(() => {
    // Cleanup function to set isMounted to false when the component unmounts
    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId.current);
    };
  }, []);

  async function dismissCheckoutErrors() {
    try {
      setError(null);
      setLoading(true);

      const response = await deleteCheckoutErrors(cartToken);

      // wenn 200 dann redirect zum checkout (da ist idempotent, ween es schon gleöscht wurde kommt auch 200 zurück)
      if (isMounted.current) {
        history.push('/checkout');
      }
    } catch (error) {
      // wenn 400 und error == "cart_token_mismatch" dann war der cart token falsch, evtl nochmal probieren
      if (isMounted.current) {
        setError(error);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }

  async function requestStatus() {
    clearTimeout(timeoutId.current);
    try {
      setError(null);
      setLoading(true);

      const response = await getConversionStatus(cartToken);
      // wenn hier ein 404 zuruuckkommt, aber das cart noch items hat, dann können wir zurück zum checkout oder der cart übersicht

      if ('checkout_errors' in response) setCheckoutErrors(new Error(response.checkout_errors));
      if ('error' in response) return setError(new Error(response.error));
      if ('status' in response) {
        setConversion(response);
        if (response.status !== ConversionStatusType.DONE) {
          response.status;
          timeoutId.current = setTimeout(requestStatus, RETRY_AFTER_MS);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    requestStatus();
    return () => clearTimeout(timeoutId.current);
  }, [cartToken]);

  if (checkoutErrors) {
    return (
      <Alert color="warning" className="text-left">
        <p>{t('CART.CHECKOUT_ERRORS.MESSAGE')}</p>
        {/* <Label htmlFor="reference-id">{t('CART.CHECKOUT_ERRORS.REFERENCE')}</Label>
        <Input id="reference-id" type="textarea" defaultValue={checkoutErrors.message} /> */}
        <Button onClick={dismissCheckoutErrors}>{t('CART.CHECKOUT_ERRORS.DISMISS')}</Button>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert color="danger" className="text-left">
        <p>{t('CART.CONVERSION.ERROR')}</p>
        {/* <Label htmlFor="reference-id">{t('CART.CONVERSION.REFERENCE_ID')}</Label>
        <Input id="reference-id" type="textarea" defaultValue={cartToken} /> */}
      </Alert>
    );
  }

  if (loading || !(conversion?.status || error) || conversion.status === ConversionStatusType.PENDING) {
    return (
      <>
        <LoadingSpinner className="mb-2" />
        <h4>{t(`CART.CONVERSION.IN_PROGRESS`)}</h4>
        <p className="text-muted">{t('CART.CONVERSION.BE_PATIENT')}</p>
      </>
    );
  }

  if ('status' in conversion && conversion.status === ConversionStatusType.DONE) {
    switch (conversion.goto) {
      case 'stripe':
        if ('payment_data' in conversion) {
          return (
            <StripeCheckout
              publishableKey={conversion.payment_data.public_key}
              sessionId={conversion.payment_data.session_id}
            />
          );
        }
        break;

      case 'thankyou': // fall-through
      case null:
        break;

      default:
        // if it is any other string
        return <AbsoluteRedirect to={conversion.goto} />;
    }

    if ('order_id' in conversion && 'simple_token' in conversion) {
      return <Redirect to={`/orders/${conversion.order_id}/xtoken/${encodeURIComponent(conversion.simple_token)}`} />;
    }
  }

  return (
    <Alert color="danger" className="text-left">
      <p>{t('CART.CONVERSION.ERROR')}</p>
      {/* <Label htmlFor="reference-id">{t('CART.CONVERSION.REFERENCE_ID')}</Label>
      <Input id="reference-id" type="textarea" defaultValue={cartToken} /> */}
    </Alert>
  );
};

export default React.memo(ConversionStatusComponent);
